import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  Check,
  Database,
  Link2,
  Loader2,
  RefreshCw,
  Search,
  Settings,
} from "lucide-react";
import { useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";

type NotionPosition = {
  notionId: string;
  jobTitle: string | null;
  hiringManagerEmail: string | null;
  stages: any[];
  linkedJob: any;
};

type NotionCandidateForJob = {
  notionId: string;
  importData: {
    name: string;
    email: string | null;
    phone: string | null;
    linkedinUrl: string | null;
    resumeUrl: string | null;
  };
  context: {
    status: string | null;
    source: string | null;
  };
  existingApplicant: { id: string; fullName: string; email: string } | null;
  isImported: boolean;
  syncState: any;
};

type CandidatesForJobResponse = {
  job: { id: string; title: string; notionId: string };
  totalFound: number;
  newCandidates: NotionCandidateForJob[];
  importedCandidates: NotionCandidateForJob[];
  stages: { id: string; name: string; order: number; type?: string }[];
};

export function LinkToNotionButton({
  jobOpeningId,
  onLinked,
  externalOpen,
  onExternalOpenChange,
}: {
  jobOpeningId: string;
  onLinked?: () => void;
  externalOpen?: boolean;
  onExternalOpenChange?: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = externalOpen !== undefined && onExternalOpenChange !== undefined;
  const open = isControlled ? externalOpen : internalOpen;
  const setOpen = isControlled ? onExternalOpenChange : setInternalOpen;
  const [selectedNotionId, setSelectedNotionId] = useState("");

  const { data, isLoading } = useQuery<{ notionPositions: NotionPosition[] }>({
    queryKey: ["/api/sync/notion/jobs"],
    enabled: open,
  });

  const linkMutation = useMutation({
    mutationFn: async ({ notionId, jobOpeningId }: { notionId: string; jobOpeningId: string }) => {
      const res = await apiRequest("POST", `/api/sync/notion/jobs/${notionId}/link`, {
        jobOpeningId,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Vaga vinculada ao Notion",
        description: "Agora você pode sincronizar candidatos.",
      });
      setOpen(false);
      setSelectedNotionId("");
      queryClient.invalidateQueries({
        queryKey: ["/api/applications/job", jobOpeningId, "kanban"],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/sync/notion"] });
      onLinked?.();
    },
    onError: (err: Error) => {
      toast({ title: "Erro ao vincular", description: err.message, variant: "destructive" });
    },
  });

  const unlinkedPositions = (data?.notionPositions || []).filter((p) => !p.linkedJob);

  return (
    <>
      {!isControlled && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpen(true)}
          data-testid="button-link-notion"
        >
          <Link2 className="w-4 h-4 mr-2" />
          Vincular ao Notion
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Vincular ao Notion</DialogTitle>
            <DialogDescription>
              Selecione uma posição do Notion para associar a esta vaga.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {isLoading ? (
              <Skeleton className="h-10" />
            ) : (
              <SearchableSelect
                options={unlinkedPositions.map((p) => ({
                  value: p.notionId,
                  label: p.jobTitle || "Sem título",
                }))}
                value={selectedNotionId}
                onValueChange={setSelectedNotionId}
                placeholder="Selecione uma posição do Notion..."
                data-testid="select-notion-position"
              />
            )}
            {!isLoading && unlinkedPositions.length === 0 && (
              <p className="text-sm text-muted-foreground mt-2">
                Nenhuma posição disponível no Notion. Todas já estão vinculadas ou não foram
                encontradas.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              data-testid="button-cancel-link-notion"
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (selectedNotionId) {
                  linkMutation.mutate({ notionId: selectedNotionId, jobOpeningId });
                }
              }}
              disabled={!selectedNotionId || linkMutation.isPending}
              data-testid="button-confirm-link-notion"
            >
              {linkMutation.isPending && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
              Vincular
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function formatStageOptions(stages: { id: string; name: string; order: number; type?: string }[]) {
  return stages.map((s) => ({
    value: s.id,
    label: s.name,
  }));
}

export function SyncNotionCandidatesButton({
  jobOpeningId,
  externalOpen,
  onExternalOpenChange,
}: {
  jobOpeningId: string;
  externalOpen?: boolean;
  onExternalOpenChange?: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = externalOpen !== undefined && onExternalOpenChange !== undefined;
  const open = isControlled ? externalOpen : internalOpen;
  const setOpen = isControlled ? onExternalOpenChange : setInternalOpen;
  const [selectedCandidates, setSelectedCandidates] = useState<Record<string, string>>({});
  const [defaultStageId, setDefaultStageId] = useState<string>("");

  const { data, isLoading, refetch } = useQuery<CandidatesForJobResponse>({
    queryKey: ["/api/sync/notion/jobs", jobOpeningId, "candidates"],
    queryFn: async () => {
      const res = await fetch(`/api/sync/notion/jobs/${jobOpeningId}/candidates`);
      if (!res.ok) throw new Error("Failed to fetch");
      return res.json();
    },
    enabled: open,
  });

  const importMutation = useMutation({
    mutationFn: async (candidates: { notionId: string; stageId: string | null }[]) => {
      const res = await apiRequest(
        "POST",
        `/api/sync/notion/jobs/${jobOpeningId}/candidates/import`,
        { candidates },
      );
      return res.json();
    },
    onSuccess: (result) => {
      const imported = result.results?.filter((r: any) => r.imported)?.length || 0;
      const errors = result.errors?.length || 0;
      toast({
        title: "Sync concluído",
        description: `${imported} candidato${imported !== 1 ? "s" : ""} importado${imported !== 1 ? "s" : ""}${errors > 0 ? `, ${errors} erro${errors !== 1 ? "s" : ""}` : ""}.`,
      });
      setSelectedCandidates({});
      refetch();
      queryClient.invalidateQueries({
        queryKey: ["/api/applications/job", jobOpeningId, "kanban"],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/sync/notion/status"] });
    },
    onError: (err: Error) => {
      toast({ title: "Erro ao importar", description: err.message, variant: "destructive" });
    },
  });

  const importAllMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        `/api/sync/notion/jobs/${jobOpeningId}/candidates/import-all`,
      );
      return res.json();
    },
    onSuccess: (result) => {
      const imported = result.results?.length || 0;
      toast({
        title: "Import concluído",
        description: `${imported} candidato${imported !== 1 ? "s" : ""} importado${imported !== 1 ? "s" : ""} na primeira etapa.`,
      });
      refetch();
      queryClient.invalidateQueries({
        queryKey: ["/api/applications/job", jobOpeningId, "kanban"],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/sync/notion/status"] });
    },
    onError: (err: Error) => {
      toast({ title: "Erro ao importar", description: err.message, variant: "destructive" });
    },
  });

  const toggleCandidate = (notionId: string) => {
    setSelectedCandidates((prev) => {
      const copy = { ...prev };
      if (copy[notionId] !== undefined) {
        delete copy[notionId];
      } else {
        copy[notionId] = defaultStageId || data?.stages[0]?.id || "";
      }
      return copy;
    });
  };

  const handleImportSelected = () => {
    const candidates = Object.entries(selectedCandidates).map(([notionId, stageId]) => ({
      notionId,
      stageId: stageId || null,
    }));
    if (candidates.length > 0) {
      importMutation.mutate(candidates);
    }
  };

  return (
    <>
      {!isControlled && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpen(true)}
          data-testid="button-sync-notion"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Sincronizar com Notion
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5" />
              Sync Notion {data?.job ? `→ "${data.job.title}"` : ""}
            </DialogTitle>
            <DialogDescription>
              {data
                ? `${data.totalFound} candidatos no Notion | ${data.newCandidates.length} novos | ${data.importedCandidates.length} já importados`
                : "Buscando candidatos..."}
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="space-y-3 py-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16" />
              ))}
            </div>
          ) : data ? (
            <div className="space-y-4 py-2">
              {data.newCandidates.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                      Novos ({data.newCandidates.length})
                    </h4>
                    {data.stages.length > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Etapa padrão:</span>
                        <SearchableSelect
                          options={formatStageOptions(data.stages)}
                          value={defaultStageId}
                          preserveOrder
                          onValueChange={(v) => {
                            setDefaultStageId(v);
                            setSelectedCandidates((prev) => {
                              const copy = { ...prev };
                              for (const k of Object.keys(copy)) {
                                copy[k] = v;
                              }
                              return copy;
                            });
                          }}
                          placeholder="Selecionar..."
                          data-testid="select-default-stage"
                        />
                      </div>
                    )}
                  </div>

                  {data.newCandidates.map((cand) => (
                    <div
                      key={cand.notionId}
                      className="p-3 border rounded-lg hover:bg-accent/50 transition-colors space-y-2"
                      data-testid={`sync-candidate-${cand.notionId}`}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={selectedCandidates[cand.notionId] !== undefined}
                          onCheckedChange={() => toggleCandidate(cand.notionId)}
                          data-testid={`checkbox-candidate-${cand.notionId}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{cand.importData.name}</div>
                          <div className="text-xs text-muted-foreground flex gap-2 flex-wrap items-center">
                            {cand.importData.email && (
                              <span className="truncate max-w-[200px]">
                                {cand.importData.email}
                              </span>
                            )}
                            {cand.context.status && (
                              <Badge variant="outline" className="text-xs shrink-0">
                                {cand.context.status}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      {selectedCandidates[cand.notionId] !== undefined &&
                        data.stages.length > 0 && (
                          <div className="pl-9">
                            <SearchableSelect
                              options={formatStageOptions(data.stages)}
                              value={selectedCandidates[cand.notionId] || ""}
                              preserveOrder
                              onValueChange={(v) =>
                                setSelectedCandidates((prev) => ({ ...prev, [cand.notionId]: v }))
                              }
                              placeholder="Selecionar etapa..."
                              data-testid={`select-stage-${cand.notionId}`}
                            />
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              )}

              {data.importedCandidates.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                    Já Importados ({data.importedCandidates.length})
                  </h4>
                  <div className="space-y-1">
                    {data.importedCandidates.map((cand) => (
                      <div
                        key={cand.notionId}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground"
                      >
                        <Check className="w-4 h-4 text-[hsl(var(--forest-pulse))] shrink-0" />
                        <span>{cand.importData.name}</span>
                        {cand.importData.email && (
                          <span className="text-xs">({cand.importData.email})</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {data.newCandidates.length === 0 && (
                <p className="text-center text-muted-foreground py-4">
                  Todos os candidatos do Notion já foram importados para esta vaga.
                </p>
              )}
            </div>
          ) : null}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              data-testid="button-cancel-sync"
            >
              Fechar
            </Button>
            {data && data.newCandidates.length > 0 && (
              <>
                <Button
                  variant="outline"
                  onClick={() => importAllMutation.mutate()}
                  disabled={importAllMutation.isPending || importMutation.isPending}
                  data-testid="button-import-all-notion"
                >
                  {importAllMutation.isPending && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
                  Importar Todos ({data.newCandidates.length})
                </Button>
                <Button
                  onClick={handleImportSelected}
                  disabled={
                    Object.keys(selectedCandidates).length === 0 || importMutation.isPending
                  }
                  data-testid="button-import-selected-notion"
                >
                  {importMutation.isPending && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
                  Importar Selecionados ({Object.keys(selectedCandidates).length})
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

type ExternalDbCandidate = {
  notionId: string;
  properties: Record<string, { type: string; value: any }>;
  existingApplicant: { id: string; fullName: string; email: string } | null;
  isImported: boolean;
  syncState: any;
};

type ExternalDbCandidatesResponse = {
  databaseId: string;
  dbProperties: { name: string; type: string }[];
  job: { id: string; title: string };
  totalFound: number;
  newCandidates: ExternalDbCandidate[];
  importedCandidates: ExternalDbCandidate[];
  stages: { id: string; name: string; order: number; type?: string }[];
};

type FieldMapping = {
  name: string;
  email: string;
  phone: string;
  linkedin: string;
};

const HIDDEN_TYPES = new Set([
  "relation",
  "rollup",
  "formula",
  "created_by",
  "last_edited_by",
  "created_time",
  "last_edited_time",
]);

function formatPropertyValue(value: any): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  if (typeof value === "boolean") return value ? "Sim" : "Não";
  if (Array.isArray(value)) {
    return value
      .map((v) => {
        if (typeof v === "string") return v;
        if (v?.name) return v.name;
        if (v?.email) return v.email;
        return String(v);
      })
      .join(", ");
  }
  return String(value);
}

function getCandidateDisplayName(cand: ExternalDbCandidate, mapping: FieldMapping): string {
  if (mapping.name && cand.properties[mapping.name]) {
    return formatPropertyValue(cand.properties[mapping.name].value) || "Sem nome";
  }
  const titleProp = Object.entries(cand.properties).find(([, p]) => p.type === "title");
  if (titleProp) return formatPropertyValue(titleProp[1].value) || "Sem nome";
  return "Sem nome";
}

function getCandidateEmail(cand: ExternalDbCandidate, mapping: FieldMapping): string | null {
  if (mapping.email && cand.properties[mapping.email]) {
    return formatPropertyValue(cand.properties[mapping.email].value) || null;
  }
  const emailProp = Object.entries(cand.properties).find(([, p]) => p.type === "email");
  if (emailProp) return formatPropertyValue(emailProp[1].value) || null;
  return null;
}

function autoDetectMapping(dbProperties: { name: string; type: string }[]): FieldMapping {
  const mapping: FieldMapping = { name: "", email: "", phone: "", linkedin: "" };

  for (const prop of dbProperties) {
    const lower = prop.name.toLowerCase();
    if (!mapping.name && prop.type === "title") mapping.name = prop.name;
    if (
      !mapping.email &&
      (prop.type === "email" || lower.includes("email") || lower.includes("e-mail"))
    )
      mapping.email = prop.name;
    if (
      !mapping.phone &&
      (prop.type === "phone_number" || lower.includes("telefone") || lower.includes("phone"))
    )
      mapping.phone = prop.name;
    if (!mapping.linkedin && lower.includes("linkedin")) mapping.linkedin = prop.name;
  }

  return mapping;
}

export function ImportFromExternalDatabaseButton({
  jobOpeningId,
  externalOpen,
  onExternalOpenChange,
}: {
  jobOpeningId: string;
  externalOpen?: boolean;
  onExternalOpenChange?: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = externalOpen !== undefined && onExternalOpenChange !== undefined;
  const open = isControlled ? externalOpen : internalOpen;
  const setOpen = isControlled ? onExternalOpenChange : setInternalOpen;

  const [databaseUrl, setDatabaseUrl] = useState("");
  const [accessError, setAccessError] = useState<string | null>(null);
  const [data, setData] = useState<ExternalDbCandidatesResponse | null>(null);
  const [selectedCandidates, setSelectedCandidates] = useState<Record<string, string>>({});
  const [defaultStageId, setDefaultStageId] = useState<string>("");
  const [fieldMapping, setFieldMapping] = useState<FieldMapping>({
    name: "",
    email: "",
    phone: "",
    linkedin: "",
  });
  const [showMapping, setShowMapping] = useState(false);
  const resetState = () => {
    setDatabaseUrl("");
    setAccessError(null);
    setData(null);
    setSelectedCandidates({});
    setDefaultStageId("");
    setFieldMapping({ name: "", email: "", phone: "", linkedin: "" });
    setShowMapping(false);
  };

  const handleOpenChange = (v: boolean) => {
    setOpen(v);
    if (!v) resetState();
  };

  const fetchMutation = useMutation({
    mutationFn: async (url: string) => {
      const res = await fetch(`/api/sync/notion/jobs/${jobOpeningId}/candidates/from-database`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ databaseUrl: url }),
        credentials: "include",
      });
      const text = await res.text();
      let parsed: any;
      try {
        parsed = JSON.parse(text);
      } catch {
        throw new Error("Erro ao buscar candidatos. Verifique se você está autenticado.");
      }
      if (!res.ok) {
        throw new Error(parsed?.error || "Erro ao buscar candidatos");
      }
      return parsed as ExternalDbCandidatesResponse;
    },
    onSuccess: (result) => {
      setAccessError(null);
      setData(result);
      const detected = autoDetectMapping(result.dbProperties);
      setFieldMapping(detected);
    },
    onError: (err: Error) => {
      setAccessError(err.message);
      setData(null);
    },
  });

  const importMutation = useMutation({
    mutationFn: async (candidates: { notionId: string; stageId: string | null }[]) => {
      const res = await apiRequest(
        "POST",
        `/api/sync/notion/jobs/${jobOpeningId}/candidates/import-from-database`,
        {
          candidates,
          databaseId: data?.databaseId,
          fieldMapping,
        },
      );
      return res.json();
    },
    onSuccess: (result) => {
      const imported = result.results?.filter((r: any) => r.imported)?.length || 0;
      const errors = result.errors?.length || 0;
      toast({
        title: "Importação concluída",
        description: `${imported} candidato${imported !== 1 ? "s" : ""} importado${imported !== 1 ? "s" : ""}${errors > 0 ? `, ${errors} erro${errors !== 1 ? "s" : ""}` : ""}.`,
      });
      setSelectedCandidates({});
      if (databaseUrl) {
        fetchMutation.mutate(databaseUrl);
      }
      queryClient.invalidateQueries({
        queryKey: ["/api/applications/job", jobOpeningId, "kanban"],
      });
    },
    onError: (err: Error) => {
      toast({ title: "Erro ao importar", description: err.message, variant: "destructive" });
    },
  });

  const toggleCandidate = (notionId: string) => {
    setSelectedCandidates((prev) => {
      const copy = { ...prev };
      if (copy[notionId] !== undefined) {
        delete copy[notionId];
      } else {
        copy[notionId] = defaultStageId || data?.stages[0]?.id || "";
      }
      return copy;
    });
  };

  const handleImportSelected = () => {
    const candidates = Object.entries(selectedCandidates).map(([notionId, stageId]) => ({
      notionId,
      stageId: stageId || null,
    }));
    if (candidates.length > 0) {
      importMutation.mutate(candidates);
    }
  };

  const mappableProperties = data?.dbProperties.filter((p) => !HIDDEN_TYPES.has(p.type)) || [];
  const mappingOptions = [
    { value: "", label: "(não mapear)" },
    ...mappableProperties.map((p) => ({ value: p.name, label: `${p.name} (${p.type})` })),
  ];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            Importar de outra database
          </DialogTitle>
          <DialogDescription>
            Cole o link de uma database do Notion para buscar candidatos e importá-los nesta vaga.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          <Input
            value={databaseUrl}
            onChange={(e) => setDatabaseUrl(e.target.value)}
            placeholder="https://www.notion.so/workspace/database-id..."
            disabled={fetchMutation.isPending}
            data-testid="input-external-database-url"
          />
          <Button
            onClick={() => {
              setAccessError(null);
              setData(null);
              setSelectedCandidates({});
              fetchMutation.mutate(databaseUrl);
            }}
            disabled={!databaseUrl.trim() || fetchMutation.isPending}
            data-testid="button-fetch-external-database"
          >
            {fetchMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin mr-1" />
            ) : (
              <Search className="w-4 h-4 mr-1" />
            )}
            Buscar
          </Button>
        </div>

        {accessError && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{accessError}</AlertDescription>
          </Alert>
        )}

        {fetchMutation.isPending && (
          <div className="space-y-3 py-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16" />
            ))}
          </div>
        )}

        {data && !fetchMutation.isPending && (
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {data.totalFound} encontrados | {data.newCandidates.length} novos |{" "}
                {data.importedCandidates.length} já importados
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowMapping(!showMapping)}
                data-testid="button-toggle-mapping"
              >
                <Settings className="w-4 h-4 mr-1" />
                {showMapping ? "Ocultar mapeamento" : "Configurar mapeamento"}
              </Button>
            </div>

            {showMapping && (
              <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
                <h4 className="text-sm font-semibold">Mapeamento de campos</h4>
                <p className="text-xs text-muted-foreground">
                  Configure qual coluna do Notion corresponde a cada campo da plataforma.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: "name" as const, label: "Nome" },
                    { key: "email" as const, label: "E-mail" },
                    { key: "phone" as const, label: "Telefone" },
                    { key: "linkedin" as const, label: "LinkedIn" },
                  ].map(({ key, label }) => (
                    <div key={key} className="space-y-1">
                      <span className="text-xs font-medium text-muted-foreground">{label}</span>
                      <SearchableSelect
                        options={mappingOptions}
                        value={fieldMapping[key]}
                        onValueChange={(v) => setFieldMapping((prev) => ({ ...prev, [key]: v }))}
                        placeholder="Selecionar coluna..."
                        data-testid={`select-mapping-${key}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.newCandidates.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                    Novos ({data.newCandidates.length})
                  </h4>
                  {data.stages.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Etapa padrão:</span>
                      <SearchableSelect
                        options={formatStageOptions(data.stages)}
                        value={defaultStageId}
                        preserveOrder
                        onValueChange={(v) => {
                          setDefaultStageId(v);
                          setSelectedCandidates((prev) => {
                            const copy = { ...prev };
                            for (const k of Object.keys(copy)) {
                              copy[k] = v;
                            }
                            return copy;
                          });
                        }}
                        placeholder="Selecionar..."
                        data-testid="select-external-default-stage"
                      />
                    </div>
                  )}
                </div>

                {data.newCandidates.map((cand) => {
                  const displayName = getCandidateDisplayName(cand, fieldMapping);
                  const displayEmail = getCandidateEmail(cand, fieldMapping);
                  const visibleProps = Object.entries(cand.properties).filter(
                    ([, p]) => !HIDDEN_TYPES.has(p.type),
                  );

                  return (
                    <div
                      key={cand.notionId}
                      className="p-3 border rounded-lg hover:bg-accent/50 transition-colors space-y-2"
                      data-testid={`external-candidate-${cand.notionId}`}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={selectedCandidates[cand.notionId] !== undefined}
                          onCheckedChange={() => toggleCandidate(cand.notionId)}
                          data-testid={`checkbox-external-candidate-${cand.notionId}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{displayName}</div>
                          {displayEmail ? (
                            <div className="text-xs text-muted-foreground truncate">
                              {displayEmail}
                            </div>
                          ) : (
                            <div className="text-xs text-[hsl(var(--amber-state))]">Sem e-mail</div>
                          )}
                        </div>
                        {data.stages.length > 0 && (
                          <div className="shrink-0 w-40">
                            <SearchableSelect
                              options={formatStageOptions(data.stages)}
                              value={selectedCandidates[cand.notionId] || defaultStageId || ""}
                              preserveOrder
                              onValueChange={(v) =>
                                setSelectedCandidates((prev) => ({ ...prev, [cand.notionId]: v }))
                              }
                              placeholder="Etapa..."
                              data-testid={`select-external-stage-${cand.notionId}`}
                            />
                          </div>
                        )}
                      </div>
                      {visibleProps.length > 0 && (
                        <div className="pl-9 grid grid-cols-2 gap-x-4 gap-y-1">
                          {visibleProps.map(([key, prop]) => {
                            const val = formatPropertyValue(prop.value);
                            if (!val) return null;
                            return (
                              <div key={key} className="text-xs overflow-hidden">
                                <span className="font-medium text-muted-foreground">{key}: </span>
                                <span className="text-foreground break-words">{val}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {data.importedCandidates.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Já Importados ({data.importedCandidates.length})
                </h4>
                <div className="space-y-1">
                  {data.importedCandidates.map((cand) => {
                    const displayName = getCandidateDisplayName(cand, fieldMapping);
                    const displayEmail = getCandidateEmail(cand, fieldMapping);
                    return (
                      <div
                        key={cand.notionId}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground"
                      >
                        <Check className="w-4 h-4 text-[hsl(var(--forest-pulse))] shrink-0" />
                        <span>{displayName}</span>
                        {displayEmail && <span className="text-xs">({displayEmail})</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {data.newCandidates.length === 0 && data.totalFound > 0 && (
              <p className="text-center text-muted-foreground py-4">
                Todos os candidatos dessa database já foram importados.
              </p>
            )}

            {data.totalFound === 0 && (
              <p className="text-center text-muted-foreground py-4">
                Nenhum candidato encontrado nessa database.
              </p>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            data-testid="button-cancel-external-import"
          >
            Fechar
          </Button>
          {data && data.newCandidates.length > 0 && (
            <Button
              onClick={handleImportSelected}
              disabled={Object.keys(selectedCandidates).length === 0 || importMutation.isPending}
              data-testid="button-import-external-selected"
            >
              {importMutation.isPending && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
              Importar Selecionados ({Object.keys(selectedCandidates).length})
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
